import React from 'react';
import { IProcurementCompany } from '../../../models/Procurement';
import { IContact, ILocation } from '../../../models/Company';
import { Dropdown, IDropdownOption, Stack, TextField } from '@fluentui/react';
import DataField from '../../Common/DataField';
import { useLazyGetContactsQuery, useGetLocationsForCompanyQuery, useGetContactByIdQuery } from '../../../store/Api';
import AutocompleteSearchBox, { ISuggestionItem } from '../../Common/AutocompleteSearchBox';
import OptionSelectionToggle from '../../Common/OptionSelectionToggle';
import { locationLabel } from '../../Contacts/CompanyLocationsDisplay';

interface ICompanyFormProps {
    company: IProcurementCompany;
    onChange: (company: IProcurementCompany) => void;
    disabled?: boolean;
    /**
     * When set, only contacts flagged for this procurement role are selectable and manual entry is
     * disabled — the address must come from a designated procurement shipping/billing contact.
     */
    restrictToProcurementContactType?: 'shipping' | 'billing';
}

const CompanyForm: React.FC<ICompanyFormProps> = (props) => {
    const [suggestions, setSuggestions] = React.useState<ISuggestionItem[]>([]);
    const [selectedContact, setSelectedContact] = React.useState<ISuggestionItem | undefined>(undefined);
    const [searchText, setSearchText] = React.useState<string>();
    const [searchContacts] = useLazyGetContactsQuery();
    const [contactResults, setContactResults] = React.useState<IContact[]>([]); // Store raw contact results for lookup
    const [manualContactEntry, setManualContactEntry] = React.useState<boolean>(false);
    // Set when a contact is selected, so we can offer that company's locations as ship-to/bill-to options.
    const [selectedContactData, setSelectedContactData] = React.useState<IContact | undefined>(undefined);
    const [desiredLocationId, setDesiredLocationId] = React.useState<string | undefined>(undefined);

    const restricted = props.restrictToProcurementContactType;

    // Rehydrate the contact when the form opens on a company that was previously picked from search
    // (e.g. reopening an existing order), so the Company Address dropdown is available again.
    const hydratedContact = useGetContactByIdQuery(
        { contactId: props.company.contactId ?? '', includeCompanyDetails: true },
        { skip: !props.company.contactId || !!selectedContactData }
    );
    const contactData = selectedContactData ?? (props.company.contactId ? hydratedContact.data : undefined);

    const companyLocations = useGetLocationsForCompanyQuery(
        { companyId: contactData?.company ?? '' },
        { skip: !contactData?.company }
    );
    const locationItems = companyLocations.data?.items ?? [];

    // Once a contact is picked and its company's locations load, default to the contact's default location,
    // falling back to the company's default — unless a location is already chosen on the order.
    React.useEffect(() => {
        if (!desiredLocationId || props.company.locationId || locationItems.length === 0) return;
        const match = locationItems.find(l => l.id === desiredLocationId);
        if (match) applyLocation(match);
    }, [desiredLocationId, locationItems.length]);

    const applyLocation = (loc?: ILocation) => {
        if (!loc) {
            // "Company address" — clear the location and fall back to the company's address.
            const addr = contactData?.companyDetails?.address;
            props.onChange({
                ...props.company,
                locationId: undefined,
                address1: addr?.address1 ?? '',
                address2: addr?.address2 ?? '',
                address3: addr?.address3 ?? '',
                city: addr?.city ?? '',
                stateTerritory: addr?.stateTerritory ?? '',
                postalCodeZip: addr?.postalCodeZip ?? '',
                country: addr?.country ?? '',
            });
            return;
        }
        props.onChange({
            ...props.company,
            locationId: loc.id,
            address1: loc.address1 ?? '',
            address2: loc.address2 ?? '',
            address3: loc.address3 ?? '',
            city: loc.city ?? '',
            stateTerritory: loc.stateTerritory ?? '',
            postalCodeZip: loc.postalCodeZip ?? '',
            country: loc.country ?? '',
        });
    };

    const locationOptions: IDropdownOption[] = [
        { key: '', text: 'Company address' },
        ...locationItems.map(l => ({ key: l.id ?? '', text: locationLabel(l) }))
    ];

    // Debounce searchText for contacts search
    React.useEffect(() => {
        if (!searchText || searchText.length < 2) return;
        const handler = setTimeout(() => {
            searchContacts({
                query: { query: searchText },
                disabled: false,
                includeCompanyDetails: true,
                procurementContactType: restricted
            })
                .then((response) => {
                    if ('data' in response) {
                        setContactResults(response.data?.items || []);
                        setSuggestions(response.data?.items.map((contact) => new ContactSearchOption(
                            contact.id || '',
                            `${contact.firstName} ${contact.lastName} (${contact.companyName})`,
                            contact.id || ''
                        )) || []);
                    }
                });
        }, 300); // 300ms debounce
        return () => clearTimeout(handler);
    }, [searchText, restricted]);

    const handleInputChange = (field: keyof IProcurementCompany, value: string) => {
        props.onChange({
            ...props.company,
            [field]: value
        });
    };

    return (
        <Stack tokens={{ childrenGap: '0.5em' }}>
            {!restricted && <div style={{marginBottom: '1em'}}>
                <OptionSelectionToggle
                    options={[
                        { key: 'search', text: 'Search Existing Contact' },
                        { key: 'manual', text: 'Enter Manually' }
                    ]}
                    selectedKey={manualContactEntry ? 'manual' : 'search'}
                    onChange={(key) => {
                        setManualContactEntry(key === 'manual');
                    }}
                />
            </div>}
            <div style={{ display: (!restricted && manualContactEntry) ? 'none' : 'block', marginBottom: '1em' }}>
                <AutocompleteSearchBox
                    styles={{
                        root: {
                            width: '100%',
                        }
                    }}
                    placeholder={restricted === 'shipping' ? 'Search procurement shipping contacts' : restricted === 'billing' ? 'Search procurement billing contacts' : 'Search Contacts'}
                    onChange={(ev, newValue) => {
                        setSearchText(newValue);
                    }}
                    value={searchText}
                    suggestions={suggestions}
                    onSuggestionClicked={(suggestion) => {
                        const item = suggestion as ISuggestionItem;
                        setSelectedContact(item);
                        // Find the contact data from contactResults
                        const contact = contactResults.find(c => c.id === item.getKey());
                        if (contact) {
                            const companyObj = contactToIProcurementCompany(contact);
                            props.onChange(companyObj);
                            // Offer the company's locations; default to the contact's, else the company's.
                            setSelectedContactData(contact);
                            setDesiredLocationId(contact.defaultLocationId ?? contact.companyDetails?.defaultLocationId);
                        }
                    }}
                    disabled={props.disabled}
                />
                <p>
                    {props.company.companyName ? <div>{props.company.companyName}</div> : null}
                    {props.company.firstName || props.company.lastName ? <div>{`${props.company.firstName || ''} ${props.company.lastName || ''}`.trim()}</div> : null}
                    {props.company.email ? <div>{props.company.email}</div> : null}
                    {props.company.phone ? <div>{props.company.phone}</div> : null}
                    <div>{props.company.address1}</div>
                    <div>{props.company.address2}</div>
                    <div>{props.company.address3}</div>
                    <div>{props.company.city} {props.company.stateTerritory} {props.company.postalCodeZip} {props.company.country}</div>
                </p>
                {contactData &&
                    <Dropdown
                        label='Company Address'
                        selectedKey={props.company.locationId ?? ''}
                        options={locationOptions}
                        disabled={props.disabled}
                        onChange={(e, option) => applyLocation(locationItems.find(l => l.id === option?.key))}
                    />}
            </div>
            <div style={{ display: (!restricted && manualContactEntry) ? 'block' : 'none', marginBottom: '1em' }}>
                <TextField
                    label='Company Name'
                    value={props.company.companyName}
                    onChange={(e, newValue) => handleInputChange('companyName', newValue || '')}
                    disabled={props.disabled}
                />
                <Stack horizontal tokens={{ childrenGap: '1em' }}>
                    <TextField
                        label='First Name'
                        value={props.company.firstName}
                        onChange={(e, newValue) => handleInputChange('firstName', newValue || '')}
                        disabled={props.disabled}
                    />
                    <TextField
                        label='Last Name'
                        value={props.company.lastName}
                        onChange={(e, newValue) => handleInputChange('lastName', newValue || '')}
                        disabled={props.disabled}
                    />
                </Stack>
                <TextField
                    label='Email'
                    value={props.company.email}
                    onChange={(e, newValue) => handleInputChange('email', newValue || '')}
                    disabled={props.disabled}
                />
                <TextField
                    label='Phone'
                    value={props.company.phone}
                    onChange={(e, newValue) => handleInputChange('phone', newValue || '')}
                    disabled={props.disabled}
                />
                <TextField
                    label='Address Line 1'
                    value={props.company.address1}
                    onChange={(e, newValue) => handleInputChange('address1', newValue || '')}
                    disabled={props.disabled}
                />
                <TextField
                    label='Address Line 2'
                    value={props.company.address2}
                    onChange={(e, newValue) => handleInputChange('address2', newValue || '')}
                    disabled={props.disabled}
                />
                <Stack horizontal tokens={{ childrenGap: '1em' }}>
                    <TextField
                        label='City'
                        value={props.company.city}
                        onChange={(e, newValue) => handleInputChange('city', newValue || '')}
                        disabled={props.disabled}
                    />
                    <TextField
                        label='State'
                        value={props.company.stateTerritory}
                        onChange={(e, newValue) => handleInputChange('stateTerritory', newValue || '')}
                        disabled={props.disabled}
                    />
                    <TextField
                        label='Postal'
                        value={props.company.postalCodeZip}
                        onChange={(e, newValue) => handleInputChange('postalCodeZip', newValue || '')}
                        disabled={props.disabled}
                    />
                </Stack>
                <TextField
                    label='Country'
                    value={props.company.country}
                    onChange={(e, newValue) => handleInputChange('country', newValue || '')}
                    disabled={props.disabled}
                />
            </div>
        </Stack>
    );
    // Utility function to convert IContact to IProcurementCompany
    function contactToIProcurementCompany(contact: IContact): IProcurementCompany {
        return {
            contactId: contact.id,
            companyName: contact.companyName || contact.company || '',
            firstName: contact.firstName || '',
            lastName: contact.lastName || '',
            email: contact.emailAddress || '',
            phone: contact.phoneNumber || '',
            address1: contact.companyDetails?.address?.address1 || '',
            address2: contact.companyDetails?.address?.address2 || '',
            city: contact.companyDetails?.address?.city || '',
            stateTerritory: contact.companyDetails?.address?.stateTerritory || '',
            postalCodeZip: contact.companyDetails?.address?.postalCodeZip || '',
            country: contact.companyDetails?.address?.country || '',
        };
    }
}

class ContactSearchOption implements ISuggestionItem {
    constructor(
        private key: string,
        private text: string,
        private value: string,
    ) { }
    getSearchText: () => string = () => {
        return this.text;
    };
    getSuggestionItem(query?: string) {
        return (
            <div key={this.key} className="suggestionItem">
                {this.text}
            </div>
        );
    }
    getKey: () => string = () => {
        return this.key;
    };
}

export default CompanyForm;